import { format, parseJSON, min, max } from 'date-fns';
import { pt } from 'date-fns/locale';
import { tryCatch } from './utils';

const brDateMask = 'dd/MM/yyyy';
const brDatetimeMask = 'dd/MM/yyyy HH:mm:ss'

/**
 * Converte uma string de data ISO em um objeto Date tratando como UTC
 * para evitar problemas de timezone que causam diferença de 1 dia.
 */
const parseUTCDate = (dateString) => {
  if (!dateString) return null;
  
  // Se a string já tem timezone (termina com Z ou +/-), usa parseJSON normalmente
  if (typeof dateString === 'string' && (dateString.endsWith('Z') || /[+-]\d{2}:\d{2}$/.test(dateString))) {
    // Remove o componente de tempo para datas que são apenas data (sem hora)
    // Isso evita problemas de conversão de timezone
    if (dateString.includes('T00:00:00')) {
      const datePart = dateString.split('T')[0];
      const [year, month, day] = datePart.split('-').map(Number);
      return new Date(year, month - 1, day);
    }
  }
  
  return parseJSON(dateString);
};

export const formatJSONDate = (jsonDate, mask) => tryCatch(() => format(parseUTCDate(jsonDate), mask, {locale: pt}));
export const brDate = (date) => tryCatch(() => format(date, brDateMask, {locale: pt}));
export const brDateTime = (date) => tryCatch(() => format(date, brDatetimeMask, {locale: pt}));

/**  Mínimo valor de um Objeto Date. */
export const dateMinValue = () => new Date(-8640000000000000);

/** Máximo valor de um Objeto Date. */
export const dateMaxValue = () => new Date(8640000000000000);

/**
 * Diz se o parâmetro é uma data válida.
 * O parâmetro tem que ser instance of Date.
 * @param {*} date data a ser avaliada (deve ser instance of Date para retornar true )
 */
export const isValidDate = (date) => date instanceof Date && !isNaN(date);

/**
 * Diz se o parâmetro equivale à uma data válida. 
 * Obs. O parâmetro não precisa ser instance of Date. 
 * Pode ser um número ou string que represente um objeto Date.
 * @param {*} date data a ser avaliada (ou número / string equivalente a uma data)
 */
export const isEquivalentToDate = (date) => (date != null && isValidDate(new Date(date)));

/**
 * Se o valor informado for equivalente à uma data válida, retorna a data do parâmetro date.
 * Senão, retorna o valor default informado.
 * @param {*} date data a ser avaliada (ou número / string equivalente a uma data)
 * @param {*} defaultValue valor retornado caso o valor informado não seja equivalente a uma data válida.
 */
export const dateFallback = (date, defaultValue) => isEquivalentToDate(date) ? new Date(date) : defaultValue;

/**
 * Se o parâmetro passado for equivalente à uma data válida, retorna a data do Parâmetro,
 * senão, retorna o Mínimo valor para um Date.
 * @param {*} date Data a ser avaliada (ou número / string equivalente a uma data)
 */
export const dateOrDateMinValue = (date) => dateFallback(date, dateMinValue());

/**
 * Se o parâmetro passado for equivalente à uma data válida, retorna a data do Parâmetro,
 * senão, retorna o Máximo valor para um Date.
 * @param {*} date Data a ser avaliada (ou número / string equivalente a uma data)
 */
export const dateOrDateMaxValue = (date) => dateFallback(date, dateMaxValue());

/**
 * Retorna a data mais antiga entre a lista de datas. Senão retorna null.
 * * Parâmetros na lista que não forem equivalentes a uma data são ignorados.
 * @param  {...any} dates Lista de datas (ou números / strings equivalentes a uma data)
 */
export const oldest = (...dates) => dateFallback(min(dates.map(d => dateFallback(d, null)).filter(d => d != null)), null);

/**
 * Retorna a data mais recente entre a lista de datas. Senão retorna null.
 * * Parâmetros na lista que não forem equivalentes a uma data são ignorados.
 * @param  {...any} dates Lista de datas (ou números / strings equivalentes a uma data)
 */
export const newest = (...dates) => dateFallback(max(dates.map(d => dateFallback(d, null)).filter(d => d != null)), null); //latest


export const timeInMs = (time) => {
  const splitTime = time?.split?.(':')?.map?.(t => Number(t));

  if (splitTime?.length !== 3 || splitTime.includes(NaN)) {
    throw new Error('Time must be in "HH:mm:ss" format');
  }

  const [hours, minutes, seconds] = splitTime;

  return (
    (hours * 60 * 60 * 1000) + 
    (minutes * 60 * 1000) +
    (seconds * 1000)
  );
}

export const currentDate = () => new Date();

export const currentTimestamp = () => new Date().getTime();

export const currentYear = () => currentDate().getFullYear();

export const parseDate = (dateString) => {
  const date = new Date(dateString);
  return isNaN(date.getTime()) ? null : date;
};

  // Função auxiliar para formatar mês e ano
export const formatMonthYear = (data) => {
    const mes = String(data.getMonth() + 1).padStart(2, '0'); // Adiciona zero à esquerda se necessário
    const ano = data.getFullYear();
    return `${mes}/${ano}`;
};

// const x = () => fns.toda
/* export const formatJSONDate = (jsonDate, mask) => {
  try {
    return format(parseJSON(jsonDate), mask)
  } catch (e) {
    return null;
  }
}; */